// client/src/utils/cacheRecovery.ts
// Utilities for clearing and recovering from cache issues

/**
 * Clear all service worker caches
 * This is useful when users experience persistent chunk loading errors
 */
export async function clearAllCaches(): Promise<boolean> {
  try {
    if (!('caches' in window)) {
      console.warn('Cache API not available');
      return false;
    }

    const cacheNames = await caches.keys();
    const deletePromises = cacheNames.map(cacheName => {
      return caches.delete(cacheName);
    });

    const results = await Promise.all(deletePromises);
    const allDeleted = results.every(r => r === true);

    if (!allDeleted) {
      console.warn('⚠️ Some caches failed to delete');
    }

    return allDeleted;
  } catch (error) {
    return false;
  }
}

/**
 * Clear session storage (reload attempt tracking)
 */
export function clearSessionStorage(): void {
  try {
    sessionStorage.clear();
  } catch (error) {
  }
}

/**
 * Clear local storage (user preferences, etc)
 * WARNING: This will clear all stored user data
 */
export function clearLocalStorage(): void {
  try {
    localStorage.clear();
  } catch (error) {
  }
}

/**
 * Perform a complete cache recovery
 * Clears all caches and reloads the page
 */
export async function performFullCacheRecovery(): Promise<void> {
  try {
    // Clear all caches
    await clearAllCaches();

    // Clear session storage (but keep auth tokens in localStorage)
    clearSessionStorage();

    // Unregister service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
      }
    }
    // Reload the page with cache bypass
    // Reload page to clear cache
    window.location.reload();
  } catch (error) {
    // Fallback: just reload
    window.location.reload();
  }
}

/**
 * Check if we're in a cache loop (repeated chunk errors)
 */
export function isInCacheLoop(): boolean {
  const loopKey = 'chunkErrorLoop';
  const loopCount = parseInt(sessionStorage.getItem(loopKey) || '0', 10);
  const loopTimestamp = parseInt(sessionStorage.getItem(`${loopKey}_time`) || '0', 10);
  const now = Date.now();

  // If more than 3 errors in the last 30 seconds, we're in a loop
  if (loopCount >= 3 && now - loopTimestamp < 30000) {
    return true;
  }

  // Reset if outside the window
  if (now - loopTimestamp > 30000) {
    sessionStorage.removeItem(loopKey);
    sessionStorage.removeItem(`${loopKey}_time`);
    return false;
  }

  return false;
}

/**
 * Record a chunk error for loop detection
 */
export function recordChunkError(): void {
  const loopKey = 'chunkErrorLoop';
  const loopCount = parseInt(sessionStorage.getItem(loopKey) || '0', 10);
  sessionStorage.setItem(loopKey, (loopCount + 1).toString());
  sessionStorage.setItem(`${loopKey}_time`, Date.now().toString());
}

